'use client'

import { useTranslations } from 'next-intl'
import { use, type RefObject } from 'react'

import SeeAllLink from '@/app/[locale]/(worlds)/components/SeeAllLink'
import Swimlane from '@/app/[locale]/(worlds)/components/Swimlane'
import ViverseWorldCard from '@/components/ViverseWorldCard'
import { CARD_CLASS_CONTINUE_PLAYING } from '@/constants/card'
import { SWIMLANE_SCROLL_BLEED_CLASS } from '@/constants/swimlane'
import { useDraggingScrollMotion } from '@/hooks/useDraggingScrollMotion'
import { Link } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { toWatchHistoryRoomInfo } from '@/lib/watch-history'
import type { RecentWatchHistoryItem } from '@/types/recent-watch-history'
import { RoomStatus } from '@/types/room-status'

type ContinuePlayingRowProps = {
  historyPromise: Promise<RecentWatchHistoryItem[] | null>
}

type ContinuePlayingCardsProps = {
  items: RecentWatchHistoryItem[]
  scrollRef: RefObject<HTMLDivElement | null>
  enableDraggingScroll: boolean
  contentInsetClassName: string
}

function ContinuePlayingCards({
  items,
  scrollRef,
  enableDraggingScroll,
  contentInsetClassName,
}: ContinuePlayingCardsProps) {
  const t = useTranslations('worlds-landing.continue-playing')
  useDraggingScrollMotion(scrollRef)

  return (
    <div
      ref={scrollRef}
      className={cn(
        SWIMLANE_SCROLL_BLEED_CLASS,
        'gap-4',
        contentInsetClassName,
        enableDraggingScroll && 'cursor-grab',
      )}
    >
      {items.map((item) => {
        const className = cn(CARD_CLASS_CONTINUE_PLAYING, 'last:mr-4 lg:last:mr-6')
        if (item.status !== RoomStatus.Available) {
          return (
            <div key={item.hub_sid} className={className} data-room-status={item.status}>
              <Link
                href={`/${item.hub_sid}`}
                prefetch={false}
                draggable={false}
                aria-label={t('unavailable')}
                className="bg-vive-surface-normal block aspect-video w-full rounded-2xl"
              />
            </div>
          )
        }

        return (
          <ViverseWorldCard
            key={item.hub_sid}
            info={toWatchHistoryRoomInfo(item)}
            href={`/${item.hub_sid}`}
            data-room-id={`continue-playing:${item.hub_sid}`}
            className={className}
            cardType="games"
            imageClassName="aspect-video w-full object-cover"
            imageSizes="220px"
          />
        )
      })}
    </div>
  )
}

export default function ContinuePlayingRow({ historyPromise }: ContinuePlayingRowProps) {
  const t = useTranslations('worlds-landing.continue-playing')
  const tCommon = useTranslations('worlds-landing.common')
  const items = use(historyPromise)

  if (!items?.length) {
    return null
  }

  return (
    <Swimlane
      data-section="continue-playing"
      className="gap-3"
      title={t('title')}
      titleAction={<SeeAllLink href="/history" label={tCommon('see-all')} />}
      renderContent={(scrollRef, enableDraggingScroll, contentInsetClassName) => (
        <ContinuePlayingCards
          items={items}
          scrollRef={scrollRef}
          enableDraggingScroll={enableDraggingScroll}
          contentInsetClassName={contentInsetClassName}
        />
      )}
    />
  )
}
